/**
 * The theme, split by whose it is (the 2026-10-06 next-phase plan, step 3, Settings with the per-org theme):
 *
 *   - the org's look: palette, logo, typography, density, radius, background pattern and the preset it started from.
 *     Saved with the org (GET/PUT /v1/platform/branding, org_branding.theme; an org admin saves it), so it applies only
 *     inside that org and switching orgs switches it. Grey-blue (DEFAULT_THEME) where the org has none;
 *   - the person's own: light, dark or the system's, kept in this browser for every org, and the legacy pack toggles
 *     the onboarding wizard still flips (the server's packs are what count);
 *   - the product's name, which is nobody's setting (PRODUCT_NAME).
 *
 * Before this the whole theme lived in one browser key (`divineiot:tenant-theme`), so a change followed a person into
 * every org. That key is read once for the person's own choices and then removed. Pure.
 */
import {
  DEFAULT_THEME,
  DENSITY_SCALE,
  PRODUCT_NAME,
  RADIUS_SCALE,
  TYPOGRAPHY_STACKS,
  type TenantTheme,
  type ThemeMode,
} from "./types";
import { THEME_PRESETS } from "./presets";

/** The org's look: what an org admin saves for the org. */
export type OrgLook = Pick<TenantTheme, "palette" | "logoUrl" | "typography" | "density" | "radius" | "pattern" | "presetId">;

/** The person's own choices. */
export interface PersonalTheme {
  mode: ThemeMode;
  enabledPacks: string[];
}

export const PERSONAL_KEY = "divineiot:theme-personal";
export const LEGACY_THEME_KEY = "divineiot:tenant-theme";
/** Where the look last read for an org is kept, so a reload paints it before the read answers. */
export const orgLookCacheKey = (orgId: string) => `divineiot:org-look:${orgId}`;

const MODES: readonly ThemeMode[] = ["light", "dark", "system"];
const TYPOGRAPHY: readonly string[] = Object.keys(TYPOGRAPHY_STACKS);
const DENSITY: readonly string[] = Object.keys(DENSITY_SCALE);
const RADIUS: readonly string[] = Object.keys(RADIUS_SCALE);
const PATTERN: readonly string[] = ["none", "dots", "grid", "topo"];
const PALETTE_SLOTS: readonly string[] = ["brand", "accent", "success", "warning", "danger", "info"];
const RGB = /^\d{1,3} \d{1,3} \d{1,3}$/;

/** "r g b" with each channel 0 to 255. */
const isRgb = (v: string) => RGB.test(v) && v.split(" ").every((c) => Number(c) <= 255);

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v);

/**
 * The look a stored blob holds, each field kept only when it reads as one: a palette of "r g b" slots (null clears a
 * slot), a logo URL, and the presets' own words. Anything else in the blob (a name, a mode, packs, an unknown key) is
 * left out, so an old or hand-written blob can never set what is not the org's.
 */
export function orgLookOf(raw: unknown): Partial<OrgLook> {
  if (!isRecord(raw)) return {};
  const out: Partial<OrgLook> = {};
  if (isRecord(raw.palette)) {
    const palette: Record<string, string | null> = {};
    for (const [slot, v] of Object.entries(raw.palette)) {
      if (!PALETTE_SLOTS.includes(slot)) continue;
      if (v === null || (typeof v === "string" && isRgb(v.trim()))) palette[slot] = v === null ? null : v.trim();
    }
    out.palette = palette as OrgLook["palette"];
  }
  if (raw.logoUrl === null || (typeof raw.logoUrl === "string" && raw.logoUrl.trim() !== "")) {
    out.logoUrl = raw.logoUrl === null ? null : raw.logoUrl.trim();
  }
  if (typeof raw.typography === "string" && TYPOGRAPHY.includes(raw.typography)) out.typography = raw.typography as OrgLook["typography"];
  if (typeof raw.density === "string" && DENSITY.includes(raw.density)) out.density = raw.density as OrgLook["density"];
  if (typeof raw.radius === "string" && RADIUS.includes(raw.radius)) out.radius = raw.radius as OrgLook["radius"];
  if (typeof raw.pattern === "string" && PATTERN.includes(raw.pattern)) out.pattern = raw.pattern as OrgLook["pattern"];
  if (typeof raw.presetId === "string" && THEME_PRESETS.some((p) => p.id === raw.presetId)) out.presetId = raw.presetId;
  return out;
}

/** Every field of a look, as the theme has it: what a save writes, so the stored look is whole. */
export function lookOfTheme(t: TenantTheme): OrgLook {
  return {
    palette: t.palette,
    logoUrl: t.logoUrl,
    typography: t.typography,
    density: t.density,
    radius: t.radius,
    pattern: t.pattern,
    presetId: t.presetId,
  };
}

/** The person's own choices a stored blob holds: its mode, and its packs; the defaults otherwise. */
export function personalOf(raw: unknown): PersonalTheme {
  const out: PersonalTheme = { mode: DEFAULT_THEME.mode, enabledPacks: DEFAULT_THEME.enabledPacks };
  if (!isRecord(raw)) return out;
  if (typeof raw.mode === "string" && MODES.includes(raw.mode as ThemeMode)) out.mode = raw.mode as ThemeMode;
  if (Array.isArray(raw.enabledPacks) && raw.enabledPacks.every((p) => typeof p === "string")) {
    out.enabledPacks = raw.enabledPacks as string[];
  }
  return out;
}

/** A look laid over a theme: its palette slot by slot, the rest field by field. */
function overLook(t: TenantTheme, look: Partial<OrgLook>): TenantTheme {
  return { ...t, ...look, palette: { ...t.palette, ...(look.palette ?? {}) } };
}

/**
 * The theme a page paints: the default, then the brand the address names before sign-in (a vendor's login page),
 * then the org's saved look, then the unsaved edits made for this org, then the person's own mode and packs, and
 * always the product's name. Edits made for another org are not this org's and are left out.
 */
export function effectiveTheme(args: {
  brand?: Partial<TenantTheme>;
  saved: Partial<OrgLook>;
  draft: { orgId: string | null; look: Partial<OrgLook> } | null;
  orgId: string | null;
  personal: PersonalTheme;
}): TenantTheme {
  let t: TenantTheme = { ...DEFAULT_THEME };
  if (args.brand) t = overLook(t, orgLookOf(args.brand));
  t = overLook(t, args.saved);
  if (args.draft && args.draft.orgId === args.orgId) t = overLook(t, args.draft.look);
  return { ...t, mode: args.personal.mode, enabledPacks: args.personal.enabledPacks, productName: PRODUCT_NAME };
}

/**
 * Whether the look shown holds edits not saved for the org: a draft made in the active org with something in it,
 * and not one just saved that is waiting for the saved look to read back (settling), which would otherwise offer
 * Save again for a moment.
 */
export function orgLookDirty(
  draft: { orgId: string | null; look: Partial<OrgLook> } | null,
  activeOrgId: string | null,
  settling: boolean,
): boolean {
  return draft !== null && !settling && draft.orgId === activeOrgId && Object.keys(draft.look).length > 0;
}

/** A preset's look, laid over the look shown now (its palette slot by slot), as the preset buttons apply it. */
export function presetLook(current: TenantTheme, presetId: string): Partial<OrgLook> | null {
  const preset = THEME_PRESETS.find((p) => p.id === presetId);
  if (!preset) return null;
  const look = orgLookOf(preset.theme);
  return { ...lookOfTheme(current), ...look, palette: { ...current.palette, ...(look.palette ?? {}) }, presetId };
}
